import { DvfModelGridMetrics } from '~/domain/dvf/dvfField';
import { DcmJsDatasetWrapper, isArrayBuffer } from './dcmJsWrapper';
import { DicomRegistration } from './dicomRegistration';
import { DVF_QA_PRIVATE_CREATOR } from './deformableRegistrationTags';
import {
    deformationMatrix,
    finiteTriple,
    finiteValues,
    modelGridMetrics,
    privateValuesFromBytes,
} from './deformableRegistrationValues';

export type DeformableGrid = {
    readonly size: [number, number, number];
    readonly spacing: [number, number, number];
    readonly origin: [number, number, number];
    readonly orientation: number[];
    readonly data: Float32Array;
    readonly preDeformationMatrix: number[];
    readonly postDeformationMatrix: number[];
    readonly metrics: DvfModelGridMetrics | null;
};

const DVF_QA_CREATOR_KEY_PATTERN = /^007100[1-9a-fA-F][0-9a-fA-F]$/;

export class DicomDeformableRegistration extends DicomRegistration {
    public readonly grid: DeformableGrid;

    constructor(
        sopUid: string,
        sourceFrameOfReferenceUid: string,
        targetFrameOfReferenceUid: string,
        contentLabel: string,
        grid: DeformableGrid,
        dataset: DcmJsDatasetWrapper
    ) {
        super(
            sopUid,
            sourceFrameOfReferenceUid,
            targetFrameOfReferenceUid,
            contentLabel,
            dataset
        );
        this.grid = grid;
    }
}

// The naturalized (keyword-addressed) dcmjs dataset; the typed dataset is read by deformableRegistrationReader.
type NaturalizedItem = Record<string, unknown>;

export function readNaturalizedRelatedFrameOfReferenceUids(
    value: unknown
): string[] {
    const dataset = asRecord(value);
    const sequence = dataset?.DeformableRegistrationSequence;
    if (!Array.isArray(sequence)) {
        return [];
    }
    const fixedFrameOfReferenceUid = dataset?.FrameOfReferenceUID;
    const related = new Set<string>();
    for (const entry of sequence) {
        const uid = asRecord(entry)?.SourceFrameOfReferenceUID;
        if (
            typeof uid === 'string' &&
            uid !== '' &&
            uid !== fixedFrameOfReferenceUid
        ) {
            related.add(uid);
        }
    }
    return [...related];
}

export function readNaturalizedDeformableGrid(
    value: unknown
): DeformableGrid | null {
    const sequence = asRecord(value)?.DeformableRegistrationSequence;
    if (!Array.isArray(sequence)) {
        return null;
    }
    for (const item of sequence) {
        const entry = asRecord(item);
        const grid = asRecord(
            firstItem(entry?.DeformableRegistrationGridSequence)
        );
        if (!entry || !grid) {
            continue;
        }
        const size = finiteTriple(asArray(grid.GridDimensions));
        const spacing = finiteTriple(asArray(grid.GridResolution));
        const origin = finiteTriple(asArray(grid.ImagePositionPatient));
        const orientation = finiteValues(
            asArray(grid.ImageOrientationPatient),
            6
        );
        const data = float32(grid.VectorGridData);
        const preDeformationMatrix = matrixOf(
            entry.PreDeformationMatrixRegistrationSequence
        );
        const postDeformationMatrix = matrixOf(
            entry.PostDeformationMatrixRegistrationSequence
        );
        // A malformed grid is skipped, never guessed.
        if (
            !size ||
            !spacing ||
            !origin ||
            !orientation ||
            !data ||
            !preDeformationMatrix ||
            !postDeformationMatrix ||
            data.length !== size[0] * size[1] * size[2] * 3
        ) {
            continue;
        }
        return {
            size,
            spacing,
            origin,
            orientation,
            data,
            preDeformationMatrix,
            postDeformationMatrix,
            metrics: readModelGridMetrics(entry),
        };
    }
    return null;
}

function readModelGridMetrics(
    item: NaturalizedItem
): DvfModelGridMetrics | null {
    const base = dvfQaPrivateBlockBase(item);
    if (base === null) {
        return null;
    }
    return modelGridMetrics((offset, count, bytesPerValue) => {
        const value = item[privateKey(base + offset)];
        const raw = firstItem(value);
        return isArrayBuffer(raw)
            ? privateValuesFromBytes(new DataView(raw), count, bytesPerValue)
            : finiteValues(Array.isArray(value) ? value : [value], count);
    });
}

function dvfQaPrivateBlockBase(item: NaturalizedItem): number | null {
    for (const key of Object.keys(item)) {
        if (!DVF_QA_CREATOR_KEY_PATTERN.test(key)) {
            continue;
        }
        const raw = firstItem(item[key]);
        if (typeof raw === 'string' && raw.trim() === DVF_QA_PRIVATE_CREATOR) {
            return parseInt(key.slice(4), 16) << 8;
        }
    }
    return null;
}

function privateKey(element: number): string {
    return `0071${element.toString(16).padStart(4, '0')}`;
}

function matrixOf(sequence: unknown): number[] | null {
    const item = asRecord(firstItem(sequence));
    return deformationMatrix(
        item !== null,
        asArray(item?.FrameOfReferenceTransformationMatrix)
    );
}

function asRecord(value: unknown): NaturalizedItem | null {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
        ? (value as NaturalizedItem)
        : null;
}

function asArray(value: unknown): readonly unknown[] | undefined {
    return Array.isArray(value) ? value : undefined;
}

function firstItem(value: unknown): unknown {
    return Array.isArray(value) ? value[0] : value;
}

function float32(value: unknown): Float32Array | null {
    const raw = firstItem(value);
    if (raw instanceof Float32Array) {
        return raw;
    }
    if (isArrayBuffer(raw) && raw.byteLength % 4 === 0) {
        return new Float32Array(raw);
    }
    return null;
}
